import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Mail } from "lucide-react";
import { Reveal, Chapter, EASE } from "@/components/Reveal";

const FAQS = [
  {
    q: "How much does a website cost?",
    a: "Core websites start from £250. Every project is quoted individually based on the pages, content and functionality you need, so you know exactly what is included before any work begins.",
  },
  {
    q: "Do I need to already have a domain or hosting?",
    a: "Not at all. We can help you choose a sensible domain and hosting setup, or work with what you already have. We will explain what you are paying for in plain English.",
  },
  {
    q: "How long does a project take?",
    a: "We aim to complete your website within 7 days, depending on the scale of the project and how quickly we receive your content and feedback.",
  },
  {
    q: "Do you update the site after launch?",
    a: "Yes. We update the site for you, and small edits are free. Full makeovers or larger changes are quoted individually so the scope stays clear.",
  },
];

export const Faq = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faqs" data-testid="faq-section" className="px-5 py-24 sm:px-8 sm:py-32 lg:px-10">
      <div className="mx-auto max-w-7xl">
        <Chapter n="05" label="Good to know" />

        <div className="mt-10 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="font-display text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-5xl">
                Questions,
                <br />
                <span className="text-[#7650c7]">answered.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 rounded-[1.5rem] bg-[#f4d35e] p-7">
                <p className="text-sm font-medium leading-relaxed text-[#201e32]">
                  Still wondering something? Ask us directly. There is no such thing as a
                  silly website question.
                </p>
                <a
                  data-testid="faq-email-link"
                  href="mailto:sales@elavatestudios.com"
                  className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#201e32] underline decoration-[#7650c7] decoration-2 underline-offset-4 transition-colors hover:text-[#7650c7]"
                >
                  <Mail size={15} aria-hidden />
                  sales@elavatestudios.com
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={0.05 + i * 0.07}>
                <div className="border-b border-[#201e32]/15">
                  <button
                    data-testid={`faq-question-${i + 1}`}
                    onClick={() => setOpen(open === i ? -1 : i)}
                    aria-expanded={open === i}
                    className="group flex w-full items-center justify-between gap-6 py-7 text-left"
                  >
                    <span className="font-display text-xl font-bold tracking-[-0.02em] transition-colors group-hover:text-[#7650c7] sm:text-2xl">
                      {f.q}
                    </span>
                    <motion.span
                      animate={{ rotate: open === i ? 45 : 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${
                        open === i ? "bg-[#7650c7] text-[#f3f0e8]" : "border border-[#201e32]/20"
                      }`}
                    >
                      <Plus size={17} aria-hidden />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <p data-testid={`faq-answer-${i + 1}`} className="max-w-[640px] pb-8 text-sm leading-relaxed text-[#6d687c] sm:text-base">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
